/**
 * Shared TypeScript type definitions for the observability feature.
 *
 * STATUS_META is the single source of truth for all status attributes.
 * Adding a new status is one line in one place — the compiler forces
 * label and color to both be filled in.
 */

export const STATUS_META = {
  healthy: { label: 'Operational', color: '#1d8102' },
  degraded: { label: 'Degraded', color: '#ff9900' },
  outage: { label: 'Outage', color: '#d13212' },
} as const;

export type HealthStatus = keyof typeof STATUS_META;

// Fallback guards against BFF returning a status not yet known to the UI.
export const statusToLabel = (status: HealthStatus): string =>
  STATUS_META[status]?.label ?? 'Unknown';

export interface DayHealth {
  date: string;
  status: HealthStatus;
}

/** Error types returned by data-fetching hooks. */
export type FetchErrorKind = 'auth' | 'network' | 'unknown';

/** Maps a rejected AtmsApiClient request to the error kind the page renders. */
export function toFetchErrorKind(err: { status?: number; response?: { status?: number } }): FetchErrorKind {
  const status = err?.status ?? err?.response?.status;
  if (status === 401 || status === 403) return 'auth';
  return status ? 'unknown' : 'network';
}

/** One category's status; 'unknown' when the BFF could not read its alarms. */
export interface CategoryStatus {
  status: HealthStatus | 'unknown';
  history: DayHealth[];
}

/** Response from GET /api/observability/status. Category ids match ALARM_CATEGORIES. */
export interface StatusResponse {
  overall: CategoryStatus;
  categories: (CategoryStatus & { id: string; label: string })[];
  fetchedAt: string;
}

export interface StatusHookState {
  isLoading: boolean;
  data: StatusResponse | null;
  error: FetchErrorKind | null;
  lastUpdated: Date | null;
}

/** CloudWatch's alarm states, plus 'MISSING' when a composite rule names an alarm that does not exist. */
export type AlarmStateValue = 'OK' | 'ALARM' | 'INSUFFICIENT_DATA' | 'MISSING';

/** One CloudWatch alarm in dev mode. `level` says which of its category's composites it feeds. */
export interface AlarmDetail {
  name: string;
  state: AlarmStateValue;
  since: string | null;
  reason: string;
  level?: 'outage' | 'degraded';
}

/** Response from GET /api/observability/alarm-details (sysadmins only), keyed by category id. */
export interface AlarmDetailsResponse {
  categories: { category: string; alarms: AlarmDetail[] }[];
  otherFiring: AlarmDetail[];
  /** True when more alarms were firing than the BFF's bounded account-wide query reads. */
  otherFiringTruncated: boolean;
  fetchedAt: string;
}
